/**
 * night_sky_schedule — the PURE deterministic scheduler behind the living
 * night sky (station transits, ambient satellites, meteors, and the
 * fleet-battle-seen-from-the-ground). Every client computes the identical
 * schedule from the wall clock (the weather/war-clock house pattern:
 * `Date.now()/1000` + an FNV-1a hash — no server state, no RNG).
 *
 * CPU-side only: SkyModule drives shader uniforms from these each frame; the
 * TSL layer (lib/sky/night_live.ts) just draws dots/streaks where told. That
 * split keeps ALL scheduling Node-testable (verify:night-sky) and the
 * fragment shader trivial.
 *
 * Realism anchors (2026-08-27 research):
 *  · A station pass reads like the ISS: a steady non-blinking Venus-bright
 *    point, 3–6 min horizon-to-horizon, occasionally FLARING near culmination
 *    and sometimes fading out MID-SKY as it enters the planet's shadow.
 *  · Ambient satellites are much fainter slow movers (mag 5-ish).
 *  · Meteors: 0.2–1 s streaks subtending 5–20°, a few per ten minutes,
 *    the occasional bright one.
 *  · The fleet battle rides the SAME war clock as the space layer
 *    (BATTLE_PERIOD_S/BATTLE_LEN_S mirrored from SpaceModule — the
 *    cross-boundary mirror precedent), so the cruiser you can spot in the
 *    night sky IS the battle a pilot is flying in right now.
 */

export interface Vec3 { x: number; y: number; z: number }

/**
 * Deterministic [0,1) hash — FNV-1a + a murmur-style avalanche finalizer.
 * WHY the finalizer: bare FNV-1a is LAST-CHARACTER-WEAK — keys that differ
 * only in a trailing cycle digit (`me1041`, `me1042`, …) leave the final
 * xor one multiply from the output, so consecutive cycles cluster within
 * ~0.5% of each other (the verify harness caught meteors at 0% and flares
 * at 83% from exactly this). The xorshift-multiply rounds give full
 * avalanche on every input bit.
 */
export function skyH(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12; h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

// ── The war clock (MIRROR of SpaceModule's module-private constants) ─────────
export const BATTLE_PERIOD_S = 1200; // a new war cycle every 20 min
export const BATTLE_LEN_S = 600;     // the war lasts 10 min of it

// ── Station transit ──────────────────────────────────────────────────────────
export const STATION_PERIOD_S = 360; // a pass window opens every 6 min (user spec)…
export const STATION_PASS_S = 240;   // …and the crossing takes 4 min of it

// ── Meteors ──────────────────────────────────────────────────────────────────
export const METEOR_BUCKET_S = 16;   // one roll every 16 s…
export const METEOR_CHANCE = 0.32;   // …streaking ~1-2 times per minute

const norm = (x: number, y: number, z: number): Vec3 => {
  const l = Math.hypot(x, y, z) || 1;
  return { x: x / l, y: y / l, z: z / l };
};
const cross = (a: Vec3, b: Vec3): Vec3 =>
  ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });

/** A unit direction from azimuth (rad) + altitude (rad, 0=horizon .. π/2=zenith). */
export function skyDir(az: number, alt: number): Vec3 {
  const c = Math.cos(alt);
  return { x: Math.sin(az) * c, y: Math.sin(alt), z: Math.cos(az) * c };
}

/**
 * The great-circle position of a transit at phase p∈[0,1]: rises at one
 * horizon point, culminates at `peakAlt`, sets opposite. Built from a basis
 * (rise direction R, and the "up-ish" vector U ⊥ R in the plane): pos =
 * R·cos(θ) + U·sin(θ) with θ sweeping 0..π across the pass.
 */
export function transitPos(riseAz: number, peakAlt: number, p: number): Vec3 {
  const R = skyDir(riseAz, 0);
  // The plane's second basis vector: at θ=π/2 the object sits at peak altitude
  // on the meridian ⊥ the rise azimuth. Tilt = peakAlt away from the zenith.
  const zenith: Vec3 = { x: 0, y: 1, z: 0 };
  const side = norm(...(Object.values(cross(zenith, R)) as [number, number, number]));
  const U = norm(
    zenith.x * Math.sin(peakAlt) + side.x * Math.cos(peakAlt),
    zenith.y * Math.sin(peakAlt) + side.y * Math.cos(peakAlt),
    zenith.z * Math.sin(peakAlt) + side.z * Math.cos(peakAlt),
  );
  const th = p * Math.PI;
  return norm(
    R.x * Math.cos(th) + U.x * Math.sin(th),
    R.y * Math.cos(th) + U.y * Math.sin(th),
    R.z * Math.cos(th) + U.z * Math.sin(th),
  );
}

export interface MoverState {
  /** Current unit direction in the sky (y up). Below horizon ⇒ not drawn. */
  dir: Vec3;
  /** Linear brightness multiplier (0 = off). */
  bright: number;
}

/**
 * The STATION pass at wall-time `nowS`. Every STATION_PERIOD_S a pass window
 * opens; the crossing occupies the first STATION_PASS_S of it. Deterministic
 * per-cycle: rise azimuth, peak altitude, a possible culmination FLARE, and a
 * ~35% chance the station enters the planet's shadow and fades out mid-pass
 * (the authentic ISS detail). Returns bright=0 outside a pass.
 */
export function stationAt(nowS: number): MoverState {
  const cyc = Math.floor(nowS / STATION_PERIOD_S);
  const t = nowS - cyc * STATION_PERIOD_S;
  if (t > STATION_PASS_S) return { dir: { x: 0, y: -1, z: 0 }, bright: 0 };
  const p = t / STATION_PASS_S;
  const riseAz = skyH(`st-az${cyc}`) * Math.PI * 2;
  const peakAlt = 0.5 + skyH(`st-alt${cyc}`) * 0.9;        // 29°..80° culmination
  const dir = transitPos(riseAz, peakAlt, p);
  // Horizon fade at both ends; a shadow exit truncates ~35% of passes.
  let b = Math.min(1, p / 0.08) * Math.min(1, (1 - p) / 0.1);
  const shadow = skyH(`st-sh${cyc}`) < 0.35;
  if (shadow) {
    const exitP = 0.55 + skyH(`st-shp${cyc}`) * 0.25;      // fades out at 55-80% across
    b *= Math.max(0, Math.min(1, (exitP - p) / 0.06));     // a ~15 s dissolve into shadow
  }
  // Culmination flare (sun glint) on ~40% of passes: ×3 for ~8 s at mid-pass.
  if (skyH(`st-fl${cyc}`) < 0.4) {
    const d = Math.abs(p - 0.5) * STATION_PASS_S;          // seconds from culmination
    b *= 1 + 2.2 * Math.max(0, 1 - d / 8);
  }
  return { dir, bright: b * 2.6 };                          // Venus-bright vs a 1.0 star
}

/** Two faint AMBIENT satellites on slow offset transits — always in motion. */
export function ambientSatsAt(nowS: number): [MoverState, MoverState] {
  const out: MoverState[] = [];
  for (let i = 0; i < 2; i++) {
    const period = 300 + i * 80;
    const cyc = Math.floor(nowS / period);
    const p = (nowS - cyc * period) / period;
    const dir = transitPos(skyH(`sat${i}-${cyc}`) * Math.PI * 2, 0.35 + skyH(`sata${i}-${cyc}`) * 1.0, p);
    const b = Math.min(1, p / 0.1) * Math.min(1, (1 - p) / 0.1) * 0.22; // barely-there mag-5 dots
    out.push({ dir, bright: b });
  }
  return out as [MoverState, MoverState];
}

export interface MeteorState {
  /** Streak head direction (unit). */
  dir: Vec3;
  /** Streak direction of travel (unit, ⊥ dir): the tail extends opposite. */
  vel: Vec3;
  /** Streak angular length so far (radians, grows over the life). */
  len: number;
  /** 0 = off; peaks fast then decays with the train. */
  bright: number;
}

/**
 * The METEOR at `nowS` (at most one active; buckets roll every
 * METEOR_BUCKET_S). Life 0.25–0.9 s + a brief fading train; arc 5–20°.
 * Deterministic per bucket — every client sees the same streak.
 */
export function meteorAt(nowS: number): MeteorState {
  const off: MeteorState = { dir: { x: 0, y: -1, z: 0 }, vel: { x: 1, y: 0, z: 0 }, len: 0, bright: 0 };
  const cyc = Math.floor(nowS / METEOR_BUCKET_S);
  if (skyH(`me${cyc}`) >= METEOR_CHANCE) return off;
  const t0 = (cyc + skyH(`met0${cyc}`) * 0.55) * METEOR_BUCKET_S; // start somewhere in the bucket
  const dur = 0.25 + skyH(`med${cyc}`) * 0.65;                     // 0.25–0.9 s of flight
  const train = 0.5 + skyH(`metr${cyc}`) * 0.7;                    // afterglow train
  const t = nowS - t0;
  if (t < 0 || t > dur + train) return off;
  const start = skyDir(skyH(`meaz${cyc}`) * Math.PI * 2, 0.25 + skyH(`meal${cyc}`) * 1.0);
  const zen: Vec3 = { x: 0, y: 1, z: 0 };
  const side = cross(start, zen);
  const tilt = (skyH(`metl${cyc}`) - 0.5) * 1.6;
  const vel = norm(
    -zen.x + side.x * tilt, -zen.y + side.y * tilt, -zen.z + side.z * tilt, // mostly downward, tilted
  );
  const arc = (5 + skyH(`mear${cyc}`) * 15) * (Math.PI / 180);              // 5–20°
  const fly = Math.min(1, t / dur);
  const head = norm(start.x + vel.x * arc * fly, start.y + vel.y * arc * fly, start.z + vel.z * arc * fly);
  const decay = t <= dur ? 1 : Math.max(0, 1 - (t - dur) / train);
  const big = skyH(`meb${cyc}`);
  const bright = decay * (big < 0.12 ? 2.4 : 0.7 + big * 0.6);              // the rare fireball
  return { dir: head, vel, len: arc * fly, bright };
}

export interface BattleSky {
  /** 0 when no battle is visible; else the war-window progress 0..1. */
  active: number;
  /** Where in the sky the engagement hangs (unit dir; low-ish). */
  dir: Vec3;
  /** The cruiser's heading tangent (unit, ⊥ dir) — the train trails behind. */
  tangent: Vec3;
  /** Seconds into the battle window (drives drift + flash hashes). */
  t: number;
  /** The war cycle id (salts the per-battle choreography). */
  cyc: number;
}

/**
 * The fleet battle AS SEEN FROM THE GROUND — the same wall-clock war window
 * SpaceModule uses to stage the sector battle (mirrored constants), so a
 * grounded player watching the night sky sees the war a pilot is fighting.
 * The engagement hangs at a deterministic spot (per cycle), drifting slowly
 * along its heading like a distant orbital convoy.
 */
export function battleSkyAt(nowS: number): BattleSky {
  const offd: Vec3 = { x: 0, y: -1, z: 0 };
  const cyc = Math.floor(nowS / BATTLE_PERIOD_S);
  const t = nowS - cyc * BATTLE_PERIOD_S;
  if (t > BATTLE_LEN_S) return { active: 0, dir: offd, tangent: { x: 1, y: 0, z: 0 }, t: 0, cyc };
  const az = skyH(`bwsky${cyc}`) * Math.PI * 2;
  const alt = 0.35 + skyH(`bwalt${cyc}`) * 0.5;              // 20°..49° — up in the sky, not overhead
  const dir0 = skyDir(az, alt);
  const zen: Vec3 = { x: 0, y: 1, z: 0 };
  const side = cross(dir0, zen);
  const l = Math.hypot(side.x, side.y, side.z) || 1;
  const tangent = { x: side.x / l, y: side.y / l, z: side.z / l };
  // The convoy drifts ~6° across the whole 10-min window — stately, orbital.
  const drift = (t / BATTLE_LEN_S - 0.5) * (6 * Math.PI / 180);
  const dir = norm(dir0.x + tangent.x * drift, dir0.y + tangent.y * drift, dir0.z + tangent.z * drift);
  return { active: Math.min(1, t / 20) * Math.min(1, (BATTLE_LEN_S - t) / 30), dir, tangent, t, cyc };
}

// ═══ PLANETS (2026-08-27 pt2): the sys0 worlds, visible from the ground ══════
// The night sky mirrors the ACTUAL home-system planets a pilot flies to
// (space_sector.ts PLANETS): Cindral (desert), Vorune (gas giant), Palewater
// (ice), and Ashfall (Cindral's moon). Each runs a circular heliocentric
// orbit; the OBSERVER world runs its own; the difference yields the planet's
// ELONGATION from the sun. Sky placement folds the diurnal rotation in the
// same way reality does: a planet at elongation ε rises/sets ε/2π of a day
// behind the sun — i.e. its sky direction is simply the game's own
// sunDirection(tod + ε/2π). Opposition (ε=π) culminates at midnight; inner
// worlds hug dusk/dawn as evening/morning stars. Periods are REAL DAYS so
// positions drift a little every night ("the orbits are happening").
import { sunDirection } from './lighting.js';

export interface PlanetDef {
  name: string;
  /** Heliocentric orbital radius (arbitrary units; observer = 1). */
  r: number;
  /** Orbital period in REAL days. */
  periodDays: number;
  /** Longitude at epoch (rad). */
  phase0: number;
  /** Disc tint (sRGB 0..1). */
  tint: { r: number; g: number; b: number };
  /** Apparent angular diameter at closest approach (deg) — the showpiece knob. */
  discDeg: number;
  /** Base brightness (Venus-class ≈ 3). */
  bright: number;
  /** MinIO planet texture key (textures/planets/<tex>.png). */
  tex: string;
  /** Moon of another planet (index into PLANET_DEFS) — orbits IT, not the sun. */
  moonOf?: number;
  /** Moon orbital radius (in elongation-radians) + period (days). */
  moonR?: number; moonDays?: number;
}

/** Observer world's own orbital period (real days) — drives outer-planet drift. */
export const OBSERVER_PERIOD_DAYS = 45;

/** PLANET DISC SIZES (2026-09-08, "in some towns there is a floating
 *  structure"): the first cut drew Vorune as a 2.4° sphere with a 1.5× halo —
 *  a 4.4° saucer hanging over a hamlet's rooftops (the Moon is 0.5°). Every
 *  disc now sits at or under the Moon: a body, never a structure. */
export const PLANET_DEFS: PlanetDef[] = [
  { name: 'Cindral',   r: 1.55, periodDays: 23,  phase0: 0.8, tint: { r: 1.0, g: 0.62, b: 0.38 }, discDeg: 0.32, bright: 2.2, tex: 'planet_desert' },
  { name: 'Vorune',    r: 3.4,  periodDays: 210, phase0: 2.4, tint: { r: 1.0, g: 0.85, b: 0.6 },  discDeg: 0.5,  bright: 3.0, tex: 'planet_gas' },
  { name: 'Palewater', r: 0.62, periodDays: 11,  phase0: 4.1, tint: { r: 0.75, g: 0.88, b: 1.0 }, discDeg: 0.28, bright: 2.6, tex: 'planet_ice' },
  { name: 'Ashfall',   r: 1.55, periodDays: 23,  phase0: 0.8, tint: { r: 0.72, g: 0.7, b: 0.66 }, discDeg: 0.22, bright: 1.1, tex: 'planet_moon', moonOf: 0, moonR: 0.05, moonDays: 3.2 },
];

export interface PlanetSky {
  name: string;
  /** Unit sky direction NOW (may be below the horizon — caller culls on y). */
  dir: Vec3;
  /** Elongation from the sun (rad, 0..2π along the sun's path). */
  elong: number;
  /** Distance-scaled brightness (0..~4). */
  bright: number;
  /** Angular diameter now (deg) — nearer = larger. */
  discDeg: number;
  tint: { r: number; g: number; b: number };
  tex: string;
}

/**
 * All planets' sky states at wall-time `nowS` for local time-of-day `tod`.
 * Pure + deterministic: every client, same night, same planets — and the next
 * night each has crept along its orbit.
 */
export function planetsAt(nowS: number, tod: number): PlanetSky[] {
  const days = nowS / 86_400;
  const lObs = (days / OBSERVER_PERIOD_DAYS) * Math.PI * 2;          // observer longitude
  const ox = Math.cos(lObs), oz = Math.sin(lObs);                     // observer at radius 1
  const out: PlanetSky[] = [];
  for (const p of PLANET_DEFS) {
    const lP = p.phase0 + (days / p.periodDays) * Math.PI * 2;
    const px = Math.cos(lP) * p.r, pz = Math.sin(lP) * p.r;
    // Geocentric vector in the ecliptic plane + the sun vector (toward origin).
    const gx = px - ox, gz = pz - oz;
    const dist = Math.hypot(gx, gz);
    const sunAng = Math.atan2(-oz, -ox);
    let elong = Math.atan2(gz, gx) - sunAng;
    elong = ((elong % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    // A moon rides its primary's elongation with a small epicycle.
    if (p.moonOf !== undefined) {
      const prim = out[p.moonOf];
      elong = prim.elong + Math.sin((days / (p.moonDays ?? 3)) * Math.PI * 2) * (p.moonR ?? 0.05);
    }
    // Diurnal fold: the planet trails the sun by elong/2π of a day.
    const d = sunDirection(tod + elong / (Math.PI * 2));
    // Brightness + disc scale with proximity (closest ≈ |r−1|, farthest ≈ r+1).
    const near = Math.abs(p.r - 1) || 0.3;
    const prox = Math.min(2.2, Math.max(0.35, near / Math.max(near, dist) * 1.6));
    out.push({
      name: p.name,
      dir: { x: d.x, y: d.y, z: d.z },
      elong,
      bright: p.bright * prox,
      discDeg: p.discDeg * Math.min(1.6, Math.max(0.45, near / Math.max(near, dist) * 1.4)),
      tint: p.tint,
      tex: p.tex,
    });
  }
  return out;
}
